import React, { useState } from 'react'
import { Input, SelectPicker } from 'rsuite'
import { FormikInputs } from './interfaces'
import { COUNTRY_DIAL_CODES } from './countryDialCodes'
import './PhoneInput.scss'

interface StandalonePhoneProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  success?: boolean;
}

function PhoneInput<V>(
  props: Readonly<FormikInputs<V> & StandalonePhoneProps>
): React.ReactElement {
  const {
    formik,
    placeholder = '',
    isDisabled,
    name,
    value: standaloneValue,
    onChange: standaloneOnChange,
    error: hasError,
    success: hasSuccess,
    className: extraClass,
    ...rest
  } = props

  // State holds ISO-2 code so each country is uniquely selectable
  const [countryIso, setCountryIso] = useState('IN')
  const [isFocused, setIsFocused] = useState(false)

  const countryOptions = COUNTRY_DIAL_CODES.map((c) => ({
    value: c.value,
    label: c.label,
    flag: c.flag,
    dialCode: c.dialCode
  }))

  const selectedCountry = COUNTRY_DIAL_CODES.find((c) => c.value === countryIso)

  const rawValue = formik
    ? (formik.values as Record<string, any>)[name] ?? ''
    : standaloneValue ?? ''

  const formatPhone = (digits: string): string => {
    const d = digits.replaceAll(/\D/g, '')
    if (d.length <= 5) return d
    return `${d.slice(0, 5)} ${d.slice(5)}`
  }

  const displayValue = isFocused ? rawValue : formatPhone(rawValue)

  const handleCountryChange = (isoCode: string | null) => {
    setCountryIso(isoCode || 'IN')
  }

  const handleChange = (val: string) => {
    const numericValue = val
      .replaceAll(/\D/g, '')
      .replace(/^0+/, '')
      .slice(0, 10)
    if (standaloneOnChange) {
      standaloneOnChange(numericValue)
    } else {
      formik?.setFieldValue(name, numericValue)
      formik?.setFieldTouched(name, true, false)
    }
  }

  const stateClasses = [
    isFocused && 'phone-input--focused',
    hasError && 'phone-input--error',
    hasSuccess && 'phone-input--success',
    extraClass
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={`phone-input ${stateClasses}`} role="group">
      <SelectPicker
        data={countryOptions}
        searchable
        cleanable={false}
        value={countryIso}
        onChange={handleCountryChange}
        className="phone-input__country-picker"
        appearance="subtle"
        menuMaxHeight={220}
        renderValue={(_value, _item) => (
          <span className="phone-input__country-value">
            {selectedCountry?.flag} {selectedCountry?.dialCode}
          </span>
        )}
      />
      <Input
        name={name}
        data-testid={name}
        type="tel"
        value={displayValue}
        disabled={isDisabled}
        placeholder={placeholder || '98765 43210'}
        className="phone-input__input"
        maxLength={12}
        onChange={handleChange}
        {...rest}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
    </div>
  )
}

export default PhoneInput
